import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  MailOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  SolutionOutlined,
  TagsOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  ConfigProvider,
  Input,
  Modal as AntModal,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
} from "antd";
import frFR from "antd/locale/fr_FR";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";
import { frenchDate } from "../../../variables/functions";
import AddExpert from "./AddExpert";

const EXPERTS_ENDPOINT = "/api/expertise/expert/all";
const PERSONNE_ENDPOINT = "/api/personne";

// Couleur de tag par type d'expert (repli neutre pour les valeurs inconnues).
const TYPE_COLORS = {
  "EXPERT MÉDICAL": "volcano",
  "EXPERT MÉCANIQUE": "blue",
  "EXPERT JUDICIAIRE": "purple",
  INVESTIGATEUR: "gold",
  "MEDECIN CONSEIL": "green",
};

const buildExpertPayload = (values, current) => {
  const { telephone, ...rest } = values;
  const payload = {
    ...rest,
    fonction: "EXPERT",
    contact: telephone ? [telephone] : [],
  };

  if (current?.codeId) {
    payload.codeId = current.codeId;
  }

  return payload;
};

const ellipsisCell = (value, maxWidth) => (
  <Tooltip title={value || "-"} placement="topLeft">
    <div
      style={{
        maxWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {value || "-"}
    </div>
  </Tooltip>
);

const Experts = () => {
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [search, setSearch] = useState("");

  const fetchExperts = () => {
    setLoading(true);
    axiosInstance
      .get(EXPERTS_ENDPOINT)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const sorted = list.sort(
          (a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0)
        );
        setExperts(sorted);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Erreur lors du chargement des experts");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchExperts();
  }, []);

  // Ouverture de la modale — création (expert = null) ou édition (expert = record).
  const handleShow = (expert = null) => {
    setSelectedExpert(expert);
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedExpert(null);
  };

  const handleSubmit = (values) => {
    const payload = buildExpertPayload(values, selectedExpert);
    setModalLoading(true);

    const request = selectedExpert
      ? axiosInstance.put(PERSONNE_ENDPOINT, JSON.stringify(payload))
      : axiosInstance.post(PERSONNE_ENDPOINT, JSON.stringify(payload));

    request
      .then(() => {
        toast.success(
          selectedExpert
            ? "Expert mis à jour avec succès"
            : "Expert ajouté avec succès"
        );
        handleCloseModal();
        fetchExperts();
      })
      .catch((err) => {
        console.error(err);
        toast.error(
          selectedExpert
            ? "Erreur lors de la mise à jour de l'expert"
            : "Erreur lors de l'ajout de l'expert"
        );
      })
      .finally(() => setModalLoading(false));
  };

  const handleDelete = (expert) => {
    AntModal.confirm({
      title: "Supprimer cet expert ?",
      icon: <ExclamationCircleFilled style={{ color: "#fa541c" }} />,
      content: `${`${expert?.nom || ""} ${expert?.prenom || ""}`.trim()} — cette action est définitive`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: () => {
        // Le DELETE historisé attend 2 segments : /{codeId}/{utilisateurId}.
        // utilisateurId = l'utilisateur connecté (tracé dans deletedBy côté backend).
        return axiosInstance
          .delete(`${PERSONNE_ENDPOINT}/${expert.codeId}/${LOGGED_USER?.codeId}`)
          .then(() => {
            toast.success("Expert supprimé avec succès");
            setExperts((prev) =>
              prev.filter((item) => item.codeId !== expert.codeId)
            );
            // Recharger depuis la base : garantit la cohérence (ex. doublon historisé résiduel).
            fetchExperts();
          })
          .catch((err) => {
            console.error(err);
            toast.error("Erreur lors de la suppression de l'expert");
            throw err;
          });
      },
    });
  };

  const filteredExperts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return experts;
    return experts.filter((e) => {
      const haystack = [
        e?.nom,
        e?.prenom,
        e?.profession,
        e?.email,
        e?.adresse,
        e?.contact?.[0],
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [experts, search]);

  const stats = useMemo(() => {
    const total = experts.length;
    const typesDistincts = new Set(
      experts.map((e) => e?.profession).filter(Boolean)
    ).size;
    const avecEmail = experts.filter((e) => e?.email).length;
    return { total, typesDistincts, avecEmail };
  }, [experts]);

  const columns = [
    {
      title: "Nom & Prénom(s)",
      dataIndex: "nom",
      key: "nom",
      width: 200,
      render: (_, record) =>
        ellipsisCell(`${record?.nom || ""} ${record?.prenom || ""}`.trim(), 200),
    },
    {
      title: "Type",
      dataIndex: "profession",
      key: "profession",
      width: 180,
      render: (value) =>
        value ? (
          <Tag color={TYPE_COLORS[value] || "default"} style={{ borderRadius: 6 }}>
            {value}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Téléphone",
      key: "telephone",
      width: 140,
      render: (_, record) => ellipsisCell(record?.contact?.[0], 140),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      width: 200,
      render: (value) => ellipsisCell(value, 200),
    },
    {
      title: "Adresse",
      dataIndex: "adresse",
      key: "adresse",
      width: 200,
      render: (value) => ellipsisCell(value, 200),
    },
    {
      title: "Créé le",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      render: (value) => (value ? <Tag>{frenchDate(value)}</Tag> : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 110,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Modifier">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleShow(record)}
            />
          </Tooltip>
          <Tooltip title="Supprimer">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <style>{`
        .experts-header-card {
          background: linear-gradient(135deg, #242166 0%, #4b45a8 100%);
          border-radius: 16px;
          padding: 28px 32px;
          margin-bottom: 24px;
          box-shadow: 0 4px 20px rgba(36, 33, 102, 0.25);
          color: #fff;
        }
        .experts-header-card h2 {
          color: #fff;
          font-size: 26px;
          font-weight: 700;
          margin: 0 0 6px 0;
        }
        .experts-header-card p { color: rgba(255,255,255,0.85); margin: 0; }
        .experts-stats-card {
          background: #fff;
          border-radius: 12px;
          padding: 20px 24px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          margin-bottom: 24px;
        }
      `}</style>

      <div className="layout-container">
        <SideBar currentMenu="PARAMETRES_SINISTRES" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper" style={{ background: "#f5f5f5" }}>
            <div className="container-xxl flex-grow-1 container-p-y">
              {/* En-tête */}
              <div className="experts-header-card">
                <h2>
                  <SolutionOutlined style={{ marginRight: 10 }} />
                  Gestion des experts
                </h2>
                <p>Experts, investigateurs et médecins-conseils intervenant sur les dossiers sinistres</p>
              </div>

              {/* Statistiques */}
              <div className="experts-stats-card">
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Total experts"
                      value={stats.total}
                      prefix={<TeamOutlined />}
                      valueStyle={{ color: "#242166" }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Types distincts"
                      value={stats.typesDistincts}
                      prefix={<TagsOutlined />}
                      valueStyle={{ color: "#1890ff" }}
                    />
                  </Col>
                  <Col xs={24} sm={8}>
                    <Statistic
                      title="Avec email"
                      value={stats.avecEmail}
                      prefix={<MailOutlined />}
                      valueStyle={{ color: "#52c41a" }}
                    />
                  </Col>
                </Row>
              </div>

              {/* Barre d'actions */}
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 12,
                  justifyContent: "space-between",
                }}
              >
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    onClick={() => handleShow(null)}
                  >
                    Ajouter un expert
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchExperts}
                    loading={loading}
                  >
                    Actualiser
                  </Button>
                </Space>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Rechercher (nom, type, email…)"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ maxWidth: 320 }}
                />
              </div>

              {/* Table */}
              <Card
                style={{
                  borderRadius: 12,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
                }}
                bodyStyle={{ padding: 0 }}
              >
                <ConfigProvider locale={frFR}>
                  <Table
                    loading={loading}
                    columns={columns}
                    dataSource={filteredExperts}
                    rowKey={(record) => record.codeId}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (t) => `Total : ${t} expert(s)`,
                    }}
                    scroll={{ x: 1200 }}
                  />
                </ConfigProvider>
              </Card>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <AddExpert
        open={modalVisible}
        loading={modalLoading}
        expert={selectedExpert}
        onCancel={handleCloseModal}
        onSubmit={handleSubmit}
      />

      <div className="layout-overlay layout-menu-toggle"></div>
      <div className="drag-target"></div>
    </div>
  );
};

export default Experts;
